import type { FieldDef } from "./types.ts";

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const NUM = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 6 });
const DATE = new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC" });
const DATETIME = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" });

export interface Choice {
  value: string;
  label?: string;
  color?: string;
}

export const choicesOf = (f: FieldDef): Choice[] => (f.options.choices as Choice[] | undefined) ?? [];
export const choiceLabel = (f: FieldDef, v: string) => choicesOf(f).find((c) => c.value === v)?.label ?? v;

/** A stable chip color per choice: its own color, or one picked from its position. */
const PALETTE = ["blue", "green", "amber", "violet", "red", "gray"];
export function choiceColor(f: FieldDef, v: string): string {
  const cs = choicesOf(f);
  const i = cs.findIndex((c) => c.value === v);
  const own = cs[i]?.color;
  return own && PALETTE.includes(own) ? own : PALETTE[(i < 0 ? 5 : i) % PALETTE.length]!;
}

export const isNumeric = (f: FieldDef) => f.interface === "number" || f.interface === "percent" || f.interface === "currency";

/** Text for a value in tables, details and cards. */
export function formatValue(f: FieldDef, v: unknown, related?: { title: string } | null): string {
  if (v === null || v === undefined || v === "") return "";
  switch (f.interface) {
    case "currency":
      return BRL.format(Number(v));
    case "number":
      return NUM.format(Number(v));
    case "percent":
      return `${NUM.format(Number(v))}%`;
    case "boolean":
      return v ? "Sim" : "Não";
    case "date":
      return DATE.format(new Date(`${String(v)}T00:00:00Z`));
    case "datetime":
      return DATETIME.format(new Date(String(v)));
    case "cpf":
      return String(v).replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
    case "cnpj":
      return String(v).replace(/^(\w{2})(\w{3})(\w{3})(\w{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    case "cep":
      return String(v).replace(/^(\d{5})(\d{3})$/, "$1-$2");
    case "phone":
      return String(v).replace(/^(\d{2})(\d{4,5})(\d{4})$/, "($1) $2-$3");
    case "select":
      return choiceLabel(f, String(v));
    case "multiSelect":
      return (v as string[]).map((x) => choiceLabel(f, x)).join(", ");
    case "relation":
      return related?.title ?? String(v).slice(0, 8);
    case "attachment":
      return (v as { name: string }[]).map((x) => x.name).join(", ");
    default:
      return String(v);
  }
}

export function formatNumber(f: FieldDef | undefined, v: number | null): string {
  if (v === null) return "—";
  if (f?.interface === "currency") return BRL.format(v);
  if (f?.interface === "percent") return `${NUM.format(v)}%`;
  return NUM.format(Math.round(v * 100) / 100);
}

export const relativeDate = (iso: string) => DATETIME.format(new Date(iso));
